import { useState } from 'react';
import type { AuditEntry } from '@shared/types';
import { api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatDateTime } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  Notice,
  PageHeader,
  Pagination,
  Select,
  Table,
  type Column,
} from '../components/ui';

interface AuditList {
  items: AuditEntry[];
  total: number;
  page: number;
  pageSize: number;
}

export function AuditPage() {
  const [page, setPage] = useState(1);
  const [result, setResult] = useState('');
  const [action, setAction] = useState('');
  const [appliedAction, setAppliedAction] = useState('');

  const audit = useAsync<AuditList>(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: '50' });
    if (result !== '') params.set('result', result);
    if (appliedAction !== '') params.set('action', appliedAction);
    return api.get(`/api/audit?${params.toString()}`);
  }, [page, result, appliedAction]);

  const columns: Column<AuditEntry>[] = [
    {
      key: 'at',
      header: '时间',
      width: '170px',
      render: (row) => <span className="text-xs text-fg-muted">{formatDateTime(row.at)}</span>,
    },
    {
      key: 'actor',
      header: '操作人',
      render: (row) => <span className="text-fg">{row.actor}</span>,
    },
    {
      key: 'action',
      header: '动作',
      render: (row) => <span className="font-mono text-xs text-fg">{row.action}</span>,
    },
    {
      key: 'target',
      header: '对象',
      render: (row) => (
        <span className="text-xs text-fg-muted">
          {row.targetType}
          {row.targetId ? ` · ${row.targetId}` : ''}
        </span>
      ),
    },
    {
      key: 'detail',
      header: '说明',
      render: (row) => (
        <span className="block max-w-[28rem] truncate text-xs text-fg-muted" title={row.detail ?? ''}>
          {row.detail ?? '—'}
        </span>
      ),
    },
    {
      key: 'result',
      header: '结果',
      render: (row) => (
        <Badge
          tone={row.result === 'success' ? 'ok' : row.result === 'denied' ? 'warn' : 'danger'}
        >
          {row.result}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="审计"
        description="这里只记后台员工的动作；客户侧的操作记录在控制台的「操作记录」里。"
      />

      {audit.error ? <Notice tone="danger">{describeError(audit.error)}</Notice> : null}

      <Card>
        <CardHeader title="员工操作" hint={`${audit.data?.total ?? 0} 条`} />
        <div className="mb-3 flex flex-wrap gap-2">
          <div className="w-36">
            <Select
              value={result}
              onChange={(value) => {
                setResult(value);
                setPage(1);
              }}
              options={[
                { value: '', label: '全部结果' },
                { value: 'success', label: '成功' },
                { value: 'denied', label: '拒绝' },
                { value: 'failure', label: '失败' },
              ]}
            />
          </div>
          <div className="w-64">
            <Input
              value={action}
              onChange={setAction}
              placeholder="动作前缀，如 order.refund"
              onEnter={() => {
                setAppliedAction(action.trim());
                setPage(1);
              }}
            />
          </div>
          <Button
            onClick={() => {
              setAppliedAction(action.trim());
              setPage(1);
            }}
          >
            搜索
          </Button>
        </div>

        <Table
          columns={columns}
          rows={audit.data?.items ?? []}
          getKey={(row) => row.id}
          loading={audit.loading}
          empty="还没有记录"
        />
        <Pagination
          page={page}
          pageSize={audit.data?.pageSize ?? 50}
          total={audit.data?.total ?? 0}
          onChange={setPage}
        />
      </Card>
    </div>
  );
}
